import React, { useState } from 'react';
import Button from '../common/Button';
import CheckInButton from './CheckInButton';

const ReservationCard = ({ reservation, onUpdate }) => {
  const [isLoading, setIsLoading] = useState(false);

  const getDisplayStatus = () => {
    const now = new Date();
    const endTime = new Date(reservation.end_time);
    const isPast = endTime < now;

    if (reservation.status === 'cancelled') {
      return { status: 'cancelled', label: 'CANCELLED' };
    } else if (isPast) {
      return { status: 'completed', label: 'COMPLETED' };
    } else if (reservation.status === 'checked_in') {
      return { status: 'checked_in', label: 'CHECKED IN' };
    } else {
      return { status: 'active', label: 'UPCOMING' };
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'bg-teal-100 text-teal-800 border-teal-500',
      'checked_in': 'bg-teal-100 text-teal-800 border-teal-500',
      'completed': 'bg-slate-100 text-slate-800 border-slate-400',
      'cancelled': 'bg-coral-100 text-coral-800 border-coral-500'
    };
    return colors[status] || colors.active;
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    let dateLabel;
    if (date.toDateString() === today.toDateString()) {
      dateLabel = 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      dateLabel = 'Tomorrow';
    } else {
      dateLabel = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    }

    return {
      date: dateLabel,
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const canCancel = () => {
    const now = new Date();
    const startTime = new Date(reservation.start_time);
    return reservation.status === 'active' && startTime > now;
  };

  const canCheckIn = () => {
    const now = new Date();
    const startTime = new Date(reservation.start_time);
    const endTime = new Date(reservation.end_time);
    const checkInWindow = 10 * 60 * 1000; // 10 minutes in milliseconds
    
    return reservation.status === 'active' && 
           now >= (startTime.getTime() - checkInWindow) && 
           now <= endTime.getTime();
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this reservation?')) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/reservations/${reservation.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel reservation');
      }

      onUpdate();
    } catch (error) {
      alert(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const startDateTime = formatDateTime(reservation.start_time);
  const endDateTime = formatDateTime(reservation.end_time);

  return (
    <div className="bg-white rounded-xl border-2 border-slate-900 shadow-retro overflow-hidden transition-all hover:shadow-retro-hover">
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {/* Date Header - More Prominent */}
            <div className="mb-4 pb-4 border-b-2 border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-br from-teal-500 to-purple-500 rounded-lg p-3 border-2 border-slate-900 shadow-retro">
                    <svg className="w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">
                      {startDateTime.date}
                    </p>
                    <p className="text-base font-semibold text-slate-700">
                      {startDateTime.time} - {endDateTime.time}
                    </p>
                  </div>
                </div>
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-xs font-bold border-2 shadow-retro ${getStatusColor(getDisplayStatus().status)}`}>
                  {getDisplayStatus().label}
                </span>
              </div>
            </div>

            {/* Board Details */}
            <div className="mb-4">
              <h3 className="text-xl font-bold text-slate-900 capitalize">
                {reservation.board_name}
              </h3>
              <p className="text-sm text-slate-600 font-medium">
                {reservation.gym_name}
              </p>
            </div>

            {/* Notes */}
            {reservation.notes && (
              <div className="mb-4 p-3 bg-purple-50 border-2 border-purple-200 rounded-lg">
                <div className="text-sm text-slate-700">
                  <span className="font-semibold text-purple-700">Notes:</span>
                  <p className="mt-1">{reservation.notes}</p>
                </div>
              </div>
            )}

            {/* Check-in Information */}
            {reservation.checked_in_at && (
              <div className="mb-4 p-3 bg-teal-50 border-2 border-teal-500 rounded-lg shadow-retro">
                <div className="text-sm text-teal-900">
                  <span className="font-bold">✓ Checked in:</span>
                  <span className="ml-1">
                    {formatDateTime(reservation.checked_in_at).date} at {formatDateTime(reservation.checked_in_at).time}
                  </span>
                </div>
              </div>
            )}

            {/* Confirmation ID and Created Date */}
            <div className="text-sm text-slate-500 font-medium">
              <span>Confirmation #{reservation.reservation_group_id}</span>
              {reservation.created_at && (
                <span className="ml-4">
                  Created: {formatDateTime(reservation.created_at).date}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons - Only show for active/upcoming reservations */}
        {getDisplayStatus().status === 'active' && (
          <div className="mt-6 flex flex-wrap gap-3">
            {canCheckIn() && (
              <CheckInButton
                reservationId={reservation.id}
                onCheckIn={onUpdate}
              />
            )}

            {canCancel() && (
              <Button
                onClick={handleCancel}
                variant="danger"
                size="small"
                loading={isLoading}
                disabled={isLoading}
              >
                Cancel Reservation
              </Button>
            )}

            <Button
              onClick={() => window.location.href = `/board/${reservation.board_id}`}
              variant="outline"
              size="small"
            >
              Update Reservation
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReservationCard;